import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  Eye, Plus, RefreshCw, Trash2, Edit2, ChevronDown, ChevronRight,
  ExternalLink, TrendingUp, Sparkles, X, AlertCircle, Clock,
  BarChart3, PieChart as PieChartIcon, Activity, Zap, Facebook, Instagram, Search, Loader2,
  Play, Pause, Music, Settings, Check, Globe, Target} from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';
import config from '../config';
import '../styles/markdown-insights.css';
import AdCard from './competitor-ads/AdCard';
import AdFilters from './competitor-ads/AdFilters';
import VerdictLargeurCiblage from './competitor-ads/VerdictLargeurCiblage';
import PayeursDsa from './competitor-ads/PayeursDsa';
import MesureNonPubliee from './competitor-ads/MesureNonPubliee';
import OngletCiblage from './competitor-ads/OngletCiblage';
import metaAdsLogo from '../logos/meta-ads.png';
import { ouvrirVisuel } from './competitor-ads/lienPublicite';

// TikTok icon component (simple SVG)
const TikTokIcon = ({ size = 16, style = {} }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    style={style}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const API_URL = config.API_URL;

// Chart colors
const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#6366f1'];

/**
 * Extract Facebook Page ID or username from various URL formats
 * Supports:
 * - https://www.facebook.com/PageName/
 * - https://facebook.com/PageName
 * - https://www.facebook.com/profile.php?id=123456789
 * - https://www.facebook.com/pages/PageName/123456789
 * - https://www.facebook.com/pg/PageName/
 * - Just username: PageName
 * - Just numeric ID: 123456789
 */
const extractFacebookPageId = (input) => {
  if (!input) return null;

  const trimmed = input.trim();

  // If it's just a numeric ID, return it
  if (/^\d+$/.test(trimmed)) {
    return { id: trimmed, type: 'numeric' };
  }

  // If it doesn't look like a URL, treat as username
  if (!trimmed.includes('facebook.com') && !trimmed.includes('/')) {
    // Remove @ if present
    const username = trimmed.replace(/^@/, '');
    return { id: username, type: 'username' };
  }

  try {
    // Parse as URL
    let url = trimmed;
    if (!url.startsWith('http')) {
      url = 'https://' + url;
    }

    const parsed = new URL(url);
    const pathname = parsed.pathname;

    // Check for profile.php?id=XXX format
    if (pathname.includes('profile.php')) {
      const id = parsed.searchParams.get('id');
      if (id) return { id, type: 'numeric' };
    }

    // Check for /pages/Name/ID format
    const pagesMatch = pathname.match(/\/pages\/[^/]+\/(\d+)/);
    if (pagesMatch) {
      return { id: pagesMatch[1], type: 'numeric' };
    }

    // Check for /pg/Name/ format
    const pgMatch = pathname.match(/\/pg\/([^/]+)/);
    if (pgMatch) {
      return { id: pgMatch[1], type: 'username' };
    }

    // Standard format: /PageName or /PageName/
    const parts = pathname.split('/').filter(p => p && p !== 'www');
    if (parts.length > 0) {
      // Skip common non-page paths
      const skipPaths = ['watch', 'events', 'photos', 'videos', 'posts', 'about', 'reviews'];
      const pagePart = parts.find(p => !skipPaths.includes(p.toLowerCase()));
      if (pagePart) {
        return { id: pagePart, type: 'username' };
      }
    }
  } catch (e) {
    // If URL parsing fails, return trimmed input as-is
    return { id: trimmed, type: 'unknown' };
  }

  return null;
};

/**
 * CompetitorAdWatchTool - Social Ad Intelligence Module
 *
 * Monitors competitor ads on Meta (Facebook/Instagram) via the Ad Library API.
 * Restricted to Kokpit organization members only.
 */
/**
 * `readOnly` : consultation seule, pour un accès client à qui l'agence a ouvert l'outil.
 * Toutes les actions d'écriture sont retirées de l'interface — le serveur les refuse de
 * son côté, mais laisser des boutons qui produisent une erreur rouge n'aurait aucun sens.
 */
const CompetitorAdWatchTool = ({ token, selectedClientId, readOnly = false }) => {
  // State - Clients & Competitors
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(selectedClientId || '');
  const [competitors, setCompetitors] = useState([]);
  const [selectedCompetitor, setSelectedCompetitor] = useState(null);
  const [loadingCompetitors, setLoadingCompetitors] = useState(false);

  // State - Ads
  const [ads, setAds] = useState([]);
  const [loadingAds, setLoadingAds] = useState(false);
  const [adsError, setAdsError] = useState(null);
  // Créa dont on a déplié les versions successives
  const [expandedCreative, setExpandedCreative] = useState(null);

  /**
   * Ouvre la créa dans un onglet, et dit ce qui a empêché de le faire.
   *
   * L'adresse est demandée au serveur : lui seul connaît le jeton Meta valide aujourd'hui, et
   * le lien enregistré porte celui du jour de la synchronisation, qui périme — voir
   * `competitor-ads/lienPublicite`.
   */
  const voirLeVisuel = async (publicite) => {
    const { ouverte, rendu, raison } = await ouvrirVisuel(publicite, token);

    if (!ouverte) {
      setError(`Impossible d'ouvrir la publicité : ${raison}`);
      return;
    }

    /**
     * Ouverte, mais pas sur la créa seule : il faut le dire.
     *
     * Sans jeton Meta valide, on retombe sur la page de l'annonceur — la publicité y est, au
     * milieu de ses voisines. Se taire laisserait croire que c'est le comportement normal, et
     * la panne de connexion resterait invisible : sur cette base, la veille n'avait plus rien
     * synchronisé depuis deux mois sans qu'aucun écran ne le signale.
     */
    if (rendu === false && raison) {
      setError(`Publicité ouverte dans la bibliothèque Meta — ${raison} pour l'afficher seule.`);
    }
  };

  // State - Stats
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // State - Summary (overview table)
  const [summaryData, setSummaryData] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // State - Filters
  // Actives par défaut : l'écran sert d'abord à voir ce qu'un concurrent diffuse
  // maintenant. Sur le compte Pébéo, « Toutes » ouvrait sur 52 pubs dont 43 arrêtées,
  // les 9 en cours étant noyées dedans.
  const [statusFilter, setStatusFilter] = useState('active');
  // « Nouvelles (7j) » n'est pas un statut : une annonce neuve peut être active ou déjà
  // arrêtée. C'est donc un filtre à part, porté par `newOnly` côté API, et non une
  // quatrième valeur du menu Statut.
  const [newOnly, setNewOnly] = useState(false);
  const [platformFilter, setPlatformFilter] = useState('');
  const [sortBy, setSortBy] = useState('startTime');
  const [dateRange, setDateRange] = useState('30');

  /**
   * Quelle carte de synthèse correspond aux filtres en cours.
   * `newOnly` l'emporte : c'est le filtre le plus restrictif des quatre.
   */
  const carteActive = newOnly ? 'nouvelles' : statusFilter;

  /**
   * Clic sur une carte de synthèse.
   *
   * La période est levée au passage, et ce n'est pas un effet de bord mais la condition
   * pour que la carte dise vrai : les compteurs sont calculés sur tout l'historique du
   * concurrent, sans notion de période. Sans cette remise à zéro, cliquer « Stoppées 23 »
   * aurait pu n'afficher aucune annonce — les 23 s'étant arrêtées avant la fenêtre — et la
   * carte aurait paru mentir. Le menu Période se met à jour à l'écran, le geste reste lisible.
   */
  const appliquerCarte = useCallback((cle) => {
    setDateRange('');
    if (cle === 'nouvelles') {
      setNewOnly(true);
      setStatusFilter('all');
    } else {
      setNewOnly(false);
      setStatusFilter(cle);
    }
  }, []);

  /**
   * Changement du menu Statut. Il relâche « Nouvelles » : les deux filtres se cumuleraient
   * sinon sans que rien ne le montre, le menu n'ayant pas de position pour « nouvelles ».
   */
  const changerStatut = useCallback((valeur) => {
    setNewOnly(false);
    setStatusFilter(valeur);
  }, []);

  // State - UI
  const [activeTab, setActiveTab] = useState('feed'); // 'feed' | 'analytics' | 'ciblage' | 'insights'
  const [showAddModal, setShowAddModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  // State - AI Insights
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  // State - Add Competitor Form
  const [newCompetitorName, setNewCompetitorName] = useState('');
  const [newPagePlatform, setNewPagePlatform] = useState('META'); // 'META' | 'TIKTOK'
  const [newPageType, setNewPageType] = useState('COMPETITOR'); // 'CLIENT' | 'COMPETITOR'
  const [newPageId, setNewPageId] = useState('');
  const [newPageLabel, setNewPageLabel] = useState('');
  const [newPageCountries, setNewPageCountries] = useState('FR');
  const [newPageFanCount, setNewPageFanCount] = useState(null);

  // State - Settings Modal
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [editingPageId, setEditingPageId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState('COMPETITOR');

  // State - Page Search
  const [pageSearchTerm, setPageSearchTerm] = useState('');
  const [pageSearchResults, setPageSearchResults] = useState([]);
  const [searchingPages, setSearchingPages] = useState(false);
  const [showPageResults, setShowPageResults] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const searchDebounceRef = useRef(null);

  // Meta réattribue un identifiant à chaque remise en ligne d'une même créa : la
  // bibliothèque renvoie donc plusieurs entrées pour un seul visuel. On regroupe sur
  // le contenu rédactionnel — titre, texte, accroche, description — et non sur les
  // images, dont les URL signées changent à chaque appel et casseraient le
  // rapprochement. Chaque groupe garde ses versions, consultables au dépliage.
  const creativeKey = useCallback((ad) => {
    const normalize = (value) => (value || '').replace(/\s+/g, ' ').trim().toLowerCase();
    const join = (list) => (list || []).map(normalize).filter(Boolean).join('\u00a6');
    return [
      join(ad.linkTitles),
      join(ad.bodies),
      join(ad.linkCaptions),
      join(ad.linkDescriptions)
    ].join('||');
  }, []);

  const groupedAds = useMemo(() => {
    const groups = new Map();
    const dateOf = (ad) => new Date(ad.adDeliveryStartTime || ad.adCreationTime || 0).getTime();

    ads.forEach((ad) => {
      const content = creativeKey(ad);
      // Une annonce sans aucun texte donne une clé faite uniquement de séparateurs :
      // les regrouper reviendrait à fusionner des créas sans rapport entre elles.
      const key = content.replace(/\|/g, '') ? content : `sans-contenu-${ad.id}`;
      if (!groups.has(key)) groups.set(key, { key, versions: [] });
      groups.get(key).versions.push(ad);
    });

    return [...groups.values()].map((group) => {
      // La version la plus récente représente la créa : la ligne affiche ses valeurs
      // telles quelles, sans additionner des reach qui compteraient deux fois les
      // mêmes personnes.
      const versions = [...group.versions].sort((a, b) => dateOf(b) - dateOf(a));
      return {
        key: group.key,
        latest: versions[0],
        versions,
        isActive: versions.some((ad) => ad.isActive)
      };
    }).sort((a, b) => dateOf(b.latest) - dateOf(a.latest));
  }, [ads, creativeKey]);

  // Load clients on mount
  //
  // Le point d'entrée du module et non `/api/client-management/clients` : ce dernier est
  // réservé aux comptes payants — un accès client y reçoit 403 — et borné au seul
  // propriétaire des données. Celui-ci applique le même contrôle de périmètre que le
  // reste de la veille, et n'expose que les cartes qui suivent au moins un concurrent.
  useEffect(() => {
    const loadClients = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/competitors/clients`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (Array.isArray(response.data?.clients)) {
          // Tri alphabétique à la source plutôt qu'au rendu : l'ordre vaut alors pour
          // tous les usages de `clients`. localeCompare gère les accents et la casse,
          // là où un tri brut placerait « Étoile » après « Zenith ».
          const sorted = [...response.data.clients].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
          );
          setClients(sorted);
        }
      } catch (err) {
        console.error('Error loading clients:', err);
      }
    };
    loadClients();
  }, [token]);

  // Update selected client when prop changes
  useEffect(() => {
    if (selectedClientId) {
      setSelectedClient(selectedClientId);
    } else {
      // Reset when header client is deselected
      setSelectedClient('');
      setSelectedCompetitor(null);
    }
  }, [selectedClientId]);

  /**
   * Un client unique n'est pas un choix : son tableau s'ouvre directement.
   *
   * L'écran s'ouvrait vide, avec un sélecteur ne proposant qu'une seule valeur — un geste
   * obligatoire dont le résultat était connu d'avance. Le cas est la règle pour un accès client,
   * qui n'en a jamais qu'un, et fréquent pour une petite agence.
   *
   * **Une seule fois, d'où le drapeau.** L'effet de remise à zéro ci-dessus vide la sélection
   * chaque fois que l'en-tête change de client ; sans ce garde-fou, revenir à « Sélectionner un
   * client » serait aussitôt défait, et le choix vide deviendrait impossible à tenir.
   */
  const preselectionFaite = useRef(false);

  useEffect(() => {
    if (preselectionFaite.current) return;
    // Un client imposé par l'en-tête a déjà tranché ; une sélection en cours aussi.
    if (selectedClientId || selectedClient) return;
    if (clients.length !== 1) return;

    preselectionFaite.current = true;
    setSelectedClient(clients[0].id);
  }, [clients, selectedClientId, selectedClient]);

  // Load competitors when client changes
  useEffect(() => {
    const loadCompetitors = async () => {
      if (!selectedClient) {
        setCompetitors([]);
        setSelectedCompetitor(null);
        setSummaryData(null);
        return;
      }

      setLoadingCompetitors(true);
      setError(null);
      setAccessDenied(false);

      try {
        const response = await axios.get(`${API_URL}/api/competitors`, {
          params: { clientId: selectedClient },
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          setCompetitors(response.data.data || []);
          // Don't auto-select - show summary table instead
          setSelectedCompetitor(null);
        }
      } catch (err) {
        if (err.response?.status === 403) {
          setAccessDenied(true);
        } else {
          setError(err.response?.data?.error || 'Erreur lors du chargement');
        }
      } finally {
        setLoadingCompetitors(false);
      }
    };

    loadCompetitors();
  }, [selectedClient, token]);

  // Load summary data
  const loadSummary = useCallback(async () => {
    if (!selectedClient) {
      setSummaryData(null);
      return;
    }

    setLoadingSummary(true);

    try {
      const response = await axios.get(`${API_URL}/api/competitors/summary`, {
        params: { clientId: selectedClient },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSummaryData(response.data.data);
      }
    } catch (err) {
      console.error('Error loading summary:', err);
    } finally {
      setLoadingSummary(false);
    }
  }, [selectedClient, token]);

  // Load summary when client changes
  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // Load ads when competitor or filters change
  const loadAds = useCallback(async () => {
    if (!selectedCompetitor) {
      setAds([]);
      return;
    }

    setLoadingAds(true);
    setAdsError(null);

    try {
      const params = {
        status: statusFilter,
        sortBy,
        sortOrder: 'desc',
        limit: 100
      };

      if (platformFilter) params.platform = platformFilter;
      if (newOnly) params.newOnly = 'true';
      if (dateRange !== '' && dateRange !== undefined) {
        const since = new Date();
        since.setDate(since.getDate() - parseInt(dateRange));
        since.setHours(0, 0, 0, 0); // Début de la journée
        params.since = since.toISOString();
      }

      const response = await axios.get(
        `${API_URL}/api/competitors/${selectedCompetitor.id}/ads`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setAds(response.data.data.ads || []);
      }
    } catch (err) {
      setAdsError(err.response?.data?.error || 'Erreur lors du chargement des pubs');
    } finally {
      setLoadingAds(false);
    }
  }, [selectedCompetitor, statusFilter, newOnly, platformFilter, sortBy, dateRange, token]);

  // Load stats when competitor changes
  const loadStats = useCallback(async () => {
    if (!selectedCompetitor) {
      setStats(null);
      return;
    }

    setLoadingStats(true);

    try {
      const response = await axios.get(
        `${API_URL}/api/competitors/${selectedCompetitor.id}/stats`,
        {
          params: { days: 30 },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    } finally {
      setLoadingStats(false);
    }
  }, [selectedCompetitor, token]);

  // Load ads and stats when competitor changes
  useEffect(() => {
    loadAds();
    loadStats();
  }, [loadAds, loadStats]);

  // Sync competitor ads
  const handleSync = async () => {
    // Consultation seule : aucune écriture depuis un accès client
    if (readOnly) return;
    if (!selectedCompetitor) return;

    setSyncing(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/competitors/${selectedCompetitor.id}/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Reload ads and stats
        await loadAds();
        await loadStats();
        // Reload summary data for the overview table
        await loadSummary();
        // Update competitors list (for lastSyncedAt)
        const compResponse = await axios.get(`${API_URL}/api/competitors`, {
          params: { clientId: selectedClient },
          headers: { Authorization: `Bearer ${token}` }
        });
        if (compResponse.data.success) {
          setCompetitors(compResponse.data.data || []);
          const updated = compResponse.data.data.find(c => c.id === selectedCompetitor.id);
          if (updated) setSelectedCompetitor(updated);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  // Generate AI insights
  const handleGenerateInsights = async () => {
    // Consultation seule : aucune écriture depuis un accès client
    if (readOnly) return;
    if (!selectedCompetitor) return;

    setLoadingInsights(true);
    setInsights(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/competitors/${selectedCompetitor.id}/insights`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setInsights(response.data.data);
      }
    } catch (err) {
      // `message` d'abord : `error` porte un code (`TOO_MANY_ANALYSES`), illisible pour qui
      // le reçoit.
      setError(err.response?.data?.message || err.response?.data?.error || 'Erreur lors de la génération des insights');
    } finally {
      setLoadingInsights(false);
    }
  };

  // Search for pages (Meta or TikTok)
  const handleSearchPages = useCallback(async (term) => {
    if (!term || term.trim().length < 2) {
      setPageSearchResults([]);
      setShowPageResults(false);
      return;
    }

    setSearchingPages(true);
    setShowPageResults(true);
    setSearchError(null);

    try {
      let response;

      if (newPagePlatform === 'TIKTOK') {
        // TikTok search - app-level auth, no user token needed
        const region = newPageCountries.split(',')[0]?.trim().toUpperCase() || 'FR';
        response = await axios.post(
          `${API_URL}/api/competitors/tiktok/search`,
          { searchTerm: term.trim(), region },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Meta search - requires user token
        const countries = newPageCountries.split(',').map(c => c.trim().toUpperCase());
        response = await axios.post(
          `${API_URL}/api/competitors/pages/search`,
          { searchTerm: term.trim(), countries, clientId: selectedClient },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (response.data.success) {
        const results = response.data.data || [];

        // If no results found for Meta platform, try direct page lookup by username
        if (results.length === 0 && newPagePlatform === 'META') {
          console.log('[Search] No results from Ad Library, trying direct page lookup...');
          try {
            const lookupResponse = await axios.post(
              `${API_URL}/api/competitors/pages/lookup`,
              { username: term.trim(), clientId: selectedClient },
              { headers: { Authorization: `Bearer ${token}` } }
            );

            if (lookupResponse.data.success && lookupResponse.data.data) {
              const page = lookupResponse.data.data;
              console.log('[Search] Found page via lookup:', page);
              // Add to results with lookup indicator
              setPageSearchResults([{
                pageId: page.pageId,
                pageName: page.pageName,
                username: page.username,
                category: page.category,
                fanCount: page.fanCount,
                fromLookup: true // Flag to indicate this came from direct lookup
              }]);
              return;
            }
          } catch (lookupErr) {
            console.error('[Search] Lookup also failed:', lookupErr.message);
            // Fall through to show empty results
          }
        }

        setPageSearchResults(results);
      }
    } catch (err) {
      console.error('Error searching pages:', err);
      setPageSearchResults([]);

      const status = err.response?.status;
      const errorMsg = err.response?.data?.error || '';
      const metaError = err.response?.data?.metaError || '';
      const tiktokError = err.response?.data?.tiktokError || '';
      const requiresReauth = err.response?.data?.requiresReauth;
      const missingConfig = err.response?.data?.missingConfig;

      console.log('[Search] Error details:', { status, errorMsg, metaError, tiktokError, requiresReauth });

      // Handle different error types
      if (newPagePlatform === 'TIKTOK') {
        // TikTok-specific errors
        if (missingConfig) {
          setSearchError('TikTok non configuré. Contactez l\'administrateur.');
        } else if (status === 429) {
          setSearchError('Trop de requêtes TikTok. Réessayez dans quelques instants.');
        } else {
          setSearchError(errorMsg || tiktokError || 'Erreur TikTok. Utilisez le mode manuel.');
          setManualMode(true);
        }
      } else {
        // Meta-specific errors
        if (status === 401 || requiresReauth) {
          setSearchError(`Token Meta expiré. ${metaError ? `(${metaError})` : 'Reconnectez votre compte.'}`);
        } else if (status === 403) {
          setSearchError(`Permissions insuffisantes. ${metaError || 'Vérifiez les droits de l\'app Meta.'}`);
          setManualMode(true);
        } else if (status === 429) {
          setSearchError('Trop de requêtes. Réessayez dans quelques instants.');
        } else if (status === 400 && errorMsg.includes('connecteur')) {
          setSearchError('Aucun compte Meta Ads connecté.');
        } else {
          setSearchError(errorMsg || metaError || 'API indisponible. Utilisez le mode manuel.');
          setManualMode(true);
        }
      }
    } finally {
      setSearchingPages(false);
    }
  }, [token, newPageCountries, selectedClient, newPagePlatform]);

  // Search and auto-select exact match when URL is pasted
  const handleLookupPage = useCallback(async (username) => {
    setSearchingPages(true);
    setShowPageResults(true);
    setSearchError(null);

    try {
      console.log('[Lookup] Searching for page with username:', username);

      // Search via Ad Library
      const countries = newPageCountries.split(',').map(c => c.trim().toUpperCase());
      const response = await axios.post(
        `${API_URL}/api/competitors/pages/search`,
        { searchTerm: username, countries, clientId: selectedClient },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const results = response.data.data || [];
        console.log('[Lookup] Search returned', results.length, 'results');

        // Try to find exact match by comparing page name (case-insensitive)
        const exactMatch = results.find(page =>
          page.pageName.toLowerCase() === username.toLowerCase() ||
          page.pageName.toLowerCase().replace(/\s+/g, '') === username.toLowerCase().replace(/\s+/g, '')
        );

        if (exactMatch) {
          console.log('[Lookup] Found exact match:', exactMatch);
          // Auto-select the exact match
          setNewPageId(exactMatch.pageId);
          setNewPageLabel(exactMatch.pageName);
          setPageSearchTerm(exactMatch.pageName);
          setShowPageResults(false);
          setSearchError(null);
          setPageSearchResults([]);
          return;
        }

        // No exact match - sort results with closest matches first
        const sortedResults = results.sort((a, b) => {
          // Prioritize pages that start with or contain the username
          const aStartsWith = a.pageName.toLowerCase().startsWith(username.toLowerCase()) ? 0 : 1;
          const bStartsWith = b.pageName.toLowerCase().startsWith(username.toLowerCase()) ? 0 : 1;
          if (aStartsWith !== bStartsWith) return aStartsWith - bStartsWith;

          const aContains = a.pageName.toLowerCase().includes(username.toLowerCase()) ? 0 : 1;
          const bContains = b.pageName.toLowerCase().includes(username.toLowerCase()) ? 0 : 1;
          return aContains - bContains;
        });

        // Clear the page ID (don't set it to the username which is invalid)
        setNewPageId('');
        setNewPageLabel('');
        setPageSearchResults(sortedResults);
        setShowPageResults(true);

        if (sortedResults.length === 0) {
          setSearchError(`Aucune page "${username}" trouvée.`);
        } else {
          setSearchError(null); // Clear error so dropdown is more visible
        }
      }
    } catch (err) {
      console.error('[Lookup] Error:', err);
      setSearchError('Erreur de recherche. Réessayez.');
    } finally {
      setSearchingPages(false);
    }
  }, [token, selectedClient, newPageCountries]);

  // Debounced page search
  const handlePageSearchInput = (value) => {
    setPageSearchTerm(value);

    // Check if user pasted a Facebook URL
    if (value.includes('facebook.com/')) {
      const extracted = extractFacebookPageId(value);
      if (extracted) {
        if (extracted.type === 'numeric') {
          // If it's a numeric ID, use it directly in manual mode
          setNewPageId(extracted.id);
          setManualMode(true);
          setSearchError(null);
          setShowPageResults(false);
          return;
        } else {
          // If it's a username, search for the page
          setPageSearchTerm(extracted.id);
          // Don't set newPageId or newPageLabel yet - wait for selection
          setNewPageId('');
          setNewPageLabel('');
          // Search for the page
          handleLookupPage(extracted.id);
          return;
        }
      }
    }

    // Clear previous timeout
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Set new timeout
    searchDebounceRef.current = setTimeout(() => {
      handleSearchPages(value);
    }, 500);
  };

  // Select a page from search results
  const handleSelectPage = (page) => {
    setNewPageId(page.pageId);
    setNewPageLabel(page.pageName);
    setNewPageFanCount(page.fanCount || null);
    setPageSearchTerm(page.pageName);
    setShowPageResults(false);
    setPageSearchResults([]);
  };

  // Add competitor/client page
  const handleAddCompetitor = async (e) => {
    e.preventDefault();
    // Consultation seule : aucune écriture depuis un accès client
    if (readOnly) return;

    if (!newCompetitorName || !newPageId || !selectedClient) {
      setError('Nom et Page/Annonceur ID requis');
      return;
    }

    // For Meta, validate that the page ID is numeric (required for Ad Library API)
    if (newPagePlatform === 'META' && !/^\d+$/.test(newPageId)) {
      setError('L\'ID de page Facebook doit être numérique. Sélectionnez une page depuis les résultats de recherche pour obtenir l\'ID correct.');
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/competitors`,
        {
          name: newCompetitorName,
          clientId: selectedClient,
          type: newPageType, // 'CLIENT' or 'COMPETITOR'
          pages: [{
            platform: newPagePlatform,
            externalId: newPageId,
            label: newPageLabel || newPageId,
            countries: newPageCountries.split(',').map(c => c.trim().toUpperCase()),
            fanCount: newPageFanCount
          }]
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setCompetitors([...competitors, response.data.data]);
        setSelectedCompetitor(response.data.data);
        setShowAddModal(false);
        setNewCompetitorName('');
        setNewPageType('COMPETITOR');
        setNewPagePlatform('META');
        setNewPageId('');
        setNewPageLabel('');
        setNewPageCountries('FR');
        setNewPageFanCount(null);
        setPageSearchTerm('');
        setPageSearchResults([]);
        setSearchError(null);
        setManualMode(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la création');
    }
  };

  // Delete competitor
  const handleDeleteCompetitor = async (competitorId) => {
    // Consultation seule : aucune écriture depuis un accès client
    if (readOnly) return;
    if (!window.confirm('Supprimer cette page et toutes ses données ?')) return;

    try {
      await axios.delete(`${API_URL}/api/competitors/${competitorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCompetitors(competitors.filter(c => c.id !== competitorId));
      if (selectedCompetitor?.id === competitorId) {
        setSelectedCompetitor(competitors[0] || null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  // Start editing a page in settings modal
  const startEditPage = (page) => {
    setEditingPageId(page.id);
    setEditName(page.name);
    setEditType(page.type || 'COMPETITOR');
  };

  // Cancel editing
  const cancelEditPage = () => {
    setEditingPageId(null);
    setEditName('');
    setEditType('COMPETITOR');
  };

  // Save page edit
  const savePageEdit = async (pageId) => {
    // Consultation seule : aucune écriture depuis un accès client
    if (readOnly) return;
    try {
      const response = await axios.put(
        `${API_URL}/api/competitors/${pageId}`,
        { name: editName, type: editType },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const updated = response.data.data;
        setCompetitors(competitors.map(c => c.id === updated.id ? { ...c, ...updated } : c));
        if (selectedCompetitor?.id === pageId) {
          setSelectedCompetitor({ ...selectedCompetitor, ...updated });
        }
        setEditingPageId(null);
        setEditName('');
        setEditType('COMPETITOR');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  // Get page URL based on platform
  const getPageUrl = (page) => {
    if (!page.pages || page.pages.length === 0) return null;
    const firstPage = page.pages[0];
    if (firstPage.platform === 'TIKTOK') {
      return `https://www.tiktok.com/@${firstPage.externalId}`;
    }
    // Meta/Facebook
    if (/^\d+$/.test(firstPage.externalId)) {
      return `https://www.facebook.com/${firstPage.externalId}`;
    }
    return `https://www.facebook.com/${firstPage.externalId}`;
  };

  // Access denied view
  if (accessDenied) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        textAlign: 'center'
      }}>
        <AlertCircle size={48} style={{ color: '#ef4444', marginBottom: '16px' }} />
        <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
          Accès réservé
        </h2>
        <p style={{ color: '#6b7280', maxWidth: '400px' }}>
          Cet outil est réservé à l'équipe Kokpit. Contactez un administrateur si vous pensez devoir y avoir accès.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img
            src={metaAdsLogo}
            alt="Meta Ads"
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              objectFit: 'contain'
            }}
          />
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: 0 }}>
              Veille concurrentielle Social Ads
            </h1>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
              Surveillez et comparez les publicités de votre marque et de vos concurrents
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          {!readOnly && (
          <button
            onClick={() => setShowAddModal(true)}
            disabled={!selectedClient}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: selectedClient ? '#3b82f6' : '#e5e7eb',
              color: selectedClient ? '#fff' : '#9ca3af',
              fontSize: '14px',
              fontWeight: '500',
              cursor: selectedClient ? 'pointer' : 'not-allowed'
            }}
          >
            <Plus size={18} />
            Ajouter une page
          </button>
          )}
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '16px',
          borderRadius: '8px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          color: '#dc2626',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <AlertCircle size={18} />
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#dc2626'
            }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Client & Competitor Selection */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        {/* Client selector - only show if no client selected from header */}
        {!selectedClientId && (
          <div style={{ minWidth: '200px' }}>
            <label style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', display: 'block' }}>
              Client
            </label>
            <select
              value={selectedClient}
              onChange={(e) => {
                setSelectedClient(e.target.value);
                setSelectedCompetitor(null);
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                fontSize: '14px',
                backgroundColor: '#fff'
              }}
            >
              {/* Pas de choix vide quand il n'y a qu'un client : « rien » n'est pas une option
                  d'une liste à une seule valeur, et l'y laisser offre le seul moyen de revenir
                  à l'écran vide que la présélection vient d'éviter. */}
              {clients.length !== 1 && <option value="">Sélectionner un client</option>}
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Page selector */}
        <div style={{ minWidth: '250px', flex: 1 }}>
          <label style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', display: 'block' }}>
            Page
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <select
              value={selectedCompetitor?.id || ''}
              onChange={(e) => {
                if (e.target.value === '') {
                  setSelectedCompetitor(null);
                } else {
                  const comp = competitors.find(c => c.id === e.target.value);
                  setSelectedCompetitor(comp || null);
                }
              }}
              disabled={loadingCompetitors || competitors.length === 0}
              style={{
                flex: 1,
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                fontSize: '14px',
                backgroundColor: '#fff'
              }}
            >
              {competitors.length === 0 ? (
                <option value="">Aucune page</option>
              ) : (
                <>
                  <option value="">Synthèse (toutes les pages)</option>
                  {competitors.map(comp => (
                    <option key={comp.id} value={comp.id}>
                      {comp.type === 'CLIENT' ? '⭐ ' : ''}{comp.name} ({comp.totalAds || 0} pubs)
                    </option>
                  ))}
                </>
              )}
            </select>

            {selectedCompetitor && !readOnly && (
              <button
                onClick={handleSync}
                disabled={syncing}
                title="Synchroniser"
                style={{
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: '#fff',
                  cursor: syncing ? 'not-allowed' : 'pointer'
                }}
              >
                <RefreshCw size={18} className={syncing ? 'spin' : ''} />
              </button>
            )}
            {selectedClient && !readOnly && (
              <button
                onClick={() => setShowSettingsModal(true)}
                title="Paramétrer les pages"
                style={{
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Settings size={18} />
              </button>
            )}
          </div>
          {selectedCompetitor?.lastSyncedAt && (
            <span style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px', display: 'block' }}>
              Dernière sync: {new Date(selectedCompetitor.lastSyncedAt).toLocaleString('fr-FR')}
            </span>
          )}
        </div>
      </div>

      {/* Summary Table - shown when client selected but no page selected */}
      {selectedClient && !selectedCompetitor && (
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          overflow: 'hidden'
        }}>
          {/* Summary table */}
          {loadingSummary && !competitors.length ? (
            <div style={{ padding: '60px 24px', textAlign: 'center', color: '#6b7280' }}>
              <RefreshCw size={24} className="spin" style={{ marginBottom: '12px' }} />
              <p>Chargement de la synthèse...</p>
            </div>
          ) : !summaryData?.pages?.length && !competitors.length ? (
            <div style={{ padding: '60px 24px', textAlign: 'center', color: '#6b7280' }}>
              <Eye size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
              <p style={{ fontSize: '15px', marginBottom: '8px' }}>Aucune page configurée</p>
              <p style={{ fontSize: '13px', marginBottom: '16px' }}>Ajoutez votre marque et vos concurrents pour commencer la veille</p>
              <button
                onClick={() => setShowAddModal(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#3b82f6',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                <Plus size={16} />
                Ajouter une page
              </button>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Page</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Followers</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Total</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Actives</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Stoppées</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Plateformes</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Nouvelles (7j)</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Durée moy.</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Dernière sync</th>
                </tr>
              </thead>
              <tbody>
                {(summaryData?.pages || competitors.map(c => ({
                  id: c.id,
                  name: c.name,
                  type: c.type || 'COMPETITOR',
                  platform: c.pages?.[0]?.platform || 'META',
                  countries: c.pages?.[0]?.countries || ['FR'],
                  totalAds: c.totalAds || 0,
                  activeAds: null,
                  stoppedAds: null,
                  newAdsLast7Days: null,
                  avgLongevityDays: null,
                  platformBreakdown: null,
                  lastSyncedAt: c.lastSyncedAt
                }))).map((page) => (
                  <tr
                    key={page.id}
                    onClick={() => {
                      const comp = competitors.find(c => c.id === page.id);
                      if (comp) setSelectedCompetitor(comp);
                    }}
                    style={{
                      borderBottom: '1px solid #f3f4f6',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {/* Page name & type */}
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          backgroundColor: page.platform === 'TIKTOK' ? '#000' : '#1877f2',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          flexShrink: 0
                        }}>
                          {page.platform === 'TIKTOK' ? (
                            <TikTokIcon size={16} style={{ color: '#fff' }} />
                          ) : (
                            <Facebook size={16} />
                          )}
                        </div>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontWeight: '600', fontSize: '14px', color: '#111827' }}>
                              {page.name}
                            </span>
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: '600',
                              backgroundColor: page.type === 'CLIENT' ? '#f0fdf4' : '#fffbeb',
                              color: page.type === 'CLIENT' ? '#22c55e' : '#f59e0b'
                            }}>
                              {page.type === 'CLIENT' ? '⭐ Ma marque' : 'Concurrent'}
                            </span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                            {page.countries?.join(', ') || 'FR'}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Followers */}
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      {page.fanCount ? (
                        <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                          {page.fanCount >= 1000000
                            ? `${(page.fanCount / 1000000).toFixed(1)}M`
                            : page.fanCount >= 1000
                              ? `${(page.fanCount / 1000).toFixed(1)}K`
                              : page.fanCount.toLocaleString('fr-FR')}
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '13px' }}>-</span>
                      )}
                    </td>

                    {/* Total */}
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <span style={{ fontSize: '15px', fontWeight: '600', color: '#374151' }}>
                        {page.totalAds}
                      </span>
                    </td>

                    {/* Active */}
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      {page.activeAds != null ? (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          backgroundColor: '#dcfce7',
                          color: '#166534',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          <Play size={10} />
                          {page.activeAds}
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '13px' }}>-</span>
                      )}
                    </td>

                    {/* Stopped */}
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      {page.stoppedAds != null ? (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          backgroundColor: '#fee2e2',
                          color: '#991b1b',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          <Pause size={10} />
                          {page.stoppedAds}
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '13px' }}>-</span>
                      )}
                    </td>

                    {/* Platform breakdown */}
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      {page.platformBreakdown && Object.keys(page.platformBreakdown).length > 0 ? (
                        (() => {
                          const platformOrder = ['facebook', 'instagram', 'messenger', 'audience_network'];
                          const platformConfig = {
                            facebook: { name: 'Facebook', color: '#1877f2' },
                            instagram: { name: 'Instagram', color: '#E4405F' },
                            messenger: { name: 'Messenger', color: '#10b981' },
                            audience_network: { name: 'Audience Network', color: '#f59e0b' }
                          };

                          // Calculer le total à partir de la somme des valeurs du breakdown
                          const total = Object.values(page.platformBreakdown).reduce((sum, v) => sum + v, 0) || 1;

                          // Préparer les données pour le pie chart dans l'ordre fixe
                          const pieData = platformOrder
                            .filter(p => page.platformBreakdown[p] > 0)
                            .map(p => ({
                              name: platformConfig[p]?.name || p,
                              value: page.platformBreakdown[p] || 0,
                              color: platformConfig[p]?.color || '#6b7280',
                              pct: Math.round((page.platformBreakdown[p] / total) * 100)
                            }));

                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center' }}>
                              {/* Pie Chart */}
                              <div style={{ width: 50, height: 50 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <PieChart>
                                    <Pie
                                      data={pieData}
                                      cx="50%"
                                      cy="50%"
                                      innerRadius={12}
                                      outerRadius={24}
                                      dataKey="value"
                                      strokeWidth={0}
                                    >
                                      {pieData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                      ))}
                                    </Pie>
                                  </PieChart>
                                </ResponsiveContainer>
                              </div>
                              {/* Labels verticaux */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-start' }}>
                                {pieData.map((entry, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      fontSize: '11px',
                                      color: '#374151'
                                    }}
                                  >
                                    <span style={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      backgroundColor: entry.color,
                                      flexShrink: 0
                                    }} />
                                    <span style={{ whiteSpace: 'nowrap' }}>
                                      {entry.name} ({entry.pct}%)
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '13px' }}>-</span>
                      )}
                    </td>

                    {/* New (7d) */}
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      {page.newAdsLast7Days != null && page.newAdsLast7Days > 0 ? (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          backgroundColor: '#fef3c7',
                          color: '#92400e',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          +{page.newAdsLast7Days}
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '13px' }}>-</span>
                      )}
                    </td>

                    {/* Avg longevity */}
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      {page.avgLongevityDays != null ? (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '13px',
                          color: page.avgLongevityDays > 30 ? '#059669' : '#6b7280',
                          fontWeight: page.avgLongevityDays > 30 ? '600' : '400'
                        }}>
                          <Clock size={12} />
                          {page.avgLongevityDays}j
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '13px' }}>-</span>
                      )}
                    </td>

                    {/* Last sync */}
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      {page.lastSyncedAt ? (
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>
                          {new Date(page.lastSyncedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#9ca3af' }}>Jamais</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Tip */}
          {summaryData?.pages?.length > 0 && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#f9fafb',
              borderTop: '1px solid #e5e7eb',
              fontSize: '12px',
              color: '#6b7280',
              textAlign: 'center'
            }}>
              Cliquez sur une ligne pour voir les détails et les publicités
            </div>
          )}
        </div>
      )}

      {/* Tabs - shown when a page is selected */}
      {selectedCompetitor && (
        <>
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '20px',
            borderBottom: '1px solid #e5e7eb',
            paddingBottom: '8px'
          }}>
            {[
              { id: 'feed', label: 'Flux Pubs', icon: Eye },
              { id: 'analytics', label: 'Analytics', icon: BarChart3 },
              // ⚠️ Ciblage reste visible en lecture seule, contrairement à Insights : ses
              // relevés SONT conservés en base, donc un accès client a quelque chose à lire.
              // Le masquer lui cacherait un travail fait pour lui.
              { id: 'ciblage', label: 'Ciblage', icon: Target },
              // Les insights ne sont pas conservés en base : sans génération possible,
              // l'onglet n'aurait jamais rien à montrer à un accès client.
              ...(readOnly ? [] : [{ id: 'insights', label: 'Insights IA', icon: Sparkles }])
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 16px',
                  borderRadius: '8px 8px 0 0',
                  border: 'none',
                  backgroundColor: activeTab === tab.id ? '#3b82f6' : 'transparent',
                  color: activeTab === tab.id ? '#fff' : '#6b7280',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Feed Tab */}
          {activeTab === 'feed' && (
            <>
              {/* Stats summary */}
              {stats && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '12px',
                  marginBottom: '20px'
                }}>
                  {[
                    { label: 'Total pubs', value: stats.totalAds, color: '#3b82f6', filtre: 'all' },
                    { label: 'Actives', value: stats.activeAds, color: '#22c55e', filtre: 'active' },
                    { label: 'Stoppées', value: stats.stoppedAds, color: '#ef4444', filtre: 'stopped' },
                    { label: 'Nouvelles (7j)', value: stats.newAdsLast7Days, color: '#f59e0b', filtre: 'nouvelles' },
                    // Une moyenne ne filtre rien : cette carte reste inerte.
                    { label: 'Durée moy.', value: `${stats.avgLongevityDays}j`, color: '#8b5cf6', filtre: null }
                  ].map((stat, idx) => {
                    const cliquable = stat.filtre !== null;
                    const selectionnee = cliquable && stat.filtre === carteActive;

                    return (
                      <div
                        key={idx}
                        onClick={() => cliquable && appliquerCarte(stat.filtre)}
                        role={cliquable ? 'button' : undefined}
                        tabIndex={cliquable ? 0 : undefined}
                        aria-pressed={cliquable ? selectionnee : undefined}
                        onKeyDown={(e) => {
                          if (cliquable && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            appliquerCarte(stat.filtre);
                          }
                        }}
                        style={{
                          padding: '16px',
                          borderRadius: '12px',
                          backgroundColor: selectionnee ? `${stat.color}0f` : '#fff',
                          // La carte retenue se signale par sa propre couleur, celle de son
                          // chiffre : sans quoi rien ne dirait quel filtre est appliqué.
                          border: `1px solid ${selectionnee ? stat.color : '#e5e7eb'}`,
                          boxShadow: selectionnee ? `inset 0 0 0 1px ${stat.color}` : 'none',
                          cursor: cliquable ? 'pointer' : 'default',
                          transition: 'background-color 0.15s ease, border-color 0.15s ease',
                          userSelect: 'none'
                        }}
                      >
                        <div style={{ fontSize: '12px', color: selectionnee ? '#111827' : '#6b7280', marginBottom: '4px' }}>
                          {stat.label}
                        </div>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: stat.color }}>
                          {stat.value}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Filters */}
              <AdFilters
                status={statusFilter}
                setStatus={changerStatut}
                platform={platformFilter}
                setPlatform={setPlatformFilter}
                sortBy={sortBy}
                setSortBy={setSortBy}
                dateRange={dateRange}
                setDateRange={setDateRange}
              />

              {/* Ads grid */}
              {loadingAds ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  <RefreshCw size={24} className="spin" style={{ marginBottom: '8px' }} />
                  <div>Chargement des publicités...</div>
                </div>
              ) : adsError ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
                  <AlertCircle size={24} style={{ marginBottom: '8px' }} />
                  <div>{adsError}</div>
                </div>
              ) : ads.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  <Eye size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
                  <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                    Aucune publicité trouvée
                  </div>
                  <div style={{ fontSize: '14px' }}>
                    Cliquez sur "Synchroniser" pour récupérer les publicités
                  </div>
                </div>
              ) : (
                <div style={{
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  overflow: 'hidden'
                }}>
                  {/* Table header */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '80px 100px 100px 120px 1fr 70px 70px 50px',
                    gap: '12px',
                    padding: '12px 16px',
                    backgroundColor: '#f9fafb',
                    borderBottom: '1px solid #e5e7eb',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase'
                  }}>
                    <span>Statut</span>
                    <span>ID Meta</span>
                    <span>Date</span>
                    <span>Plateformes</span>
                    <span>Contenu</span>
                    <span>Durée</span>
                    <span>Reach</span>
                    <span></span>
                  </div>

                  {/* Table rows */}
                  {groupedAds.map(group => {
                    const ad = group.latest;
                    const versionCount = group.versions.length;
                    const isExpanded = expandedCreative === group.key;
                    const primaryBody = ad.bodies?.[0] || '';
                    const truncatedBody = primaryBody.length > 150
                      ? primaryBody.substring(0, 150) + '...'
                      : primaryBody;
                    const primaryTitle = ad.linkTitles?.[0] || '';
                    const publishDate = ad.adDeliveryStartTime || ad.adCreationTime;
                    const formattedDate = publishDate
                      ? new Date(publishDate).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })
                      : '-';
                    const reach = ad.euTotalReach
                      ? (ad.euTotalReach >= 1000000
                          ? `${(ad.euTotalReach / 1000000).toFixed(1)}M`
                          : ad.euTotalReach >= 1000
                            ? `${(ad.euTotalReach / 1000).toFixed(0)}K`
                            : ad.euTotalReach)
                      : '-';

                    return (
                      <React.Fragment key={group.key}>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '80px 100px 100px 120px 1fr 70px 70px 50px',
                          gap: '12px',
                          padding: '14px 16px',
                          borderBottom: '1px solid #f3f4f6',
                          alignItems: 'center',
                          cursor: 'pointer',
                          transition: 'background-color 0.15s ease'
                        }}
                        onClick={() => {
                          // Plusieurs mises en ligne : le clic déplie l'historique.
                          // Une seule : il ouvre la publicité sur place.
                          if (versionCount > 1) {
                            setExpandedCreative(isExpanded ? null : group.key);
                          } else {
                            voirLeVisuel(ad);
                          }
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        {/* Status */}
                        <div>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '600',
                            backgroundColor: ad.isActive ? '#dcfce7' : '#fee2e2',
                            color: ad.isActive ? '#166534' : '#991b1b'
                          }}>
                            {ad.isActive ? <Play size={10} /> : <Pause size={10} />}
                            {ad.isActive ? 'Active' : 'Stop'}
                          </span>
                        </div>

                        {/* Meta ID */}
                        <div style={{
                          fontSize: '11px',
                          color: '#9ca3af',
                          fontFamily: 'monospace',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={ad.externalAdId}
                        >
                          {ad.externalAdId ? `...${ad.externalAdId.slice(-6)}` : '-'}
                        </div>

                        {/* Date */}
                        <div style={{ fontSize: '13px', color: '#374151' }}>
                          {formattedDate}
                        </div>

                        {/* Platforms */}
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {(ad.publisherPlatforms || []).slice(0, 3).map((platform, idx) => (
                            <span
                              key={idx}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '22px',
                                height: '22px',
                                borderRadius: '4px',
                                backgroundColor: platform === 'facebook' ? '#1877f2'
                                  : platform === 'instagram' ? '#E4405F'
                                  : platform === 'messenger' ? '#0084ff'
                                  : platform === 'tiktok' ? '#000'
                                  : '#6b7280',
                                color: '#fff',
                                fontSize: '10px'
                              }}
                              title={platform}
                            >
                              {platform === 'facebook' ? <Facebook size={12} />
                                : platform === 'instagram' ? <Instagram size={12} />
                                : platform === 'tiktok' ? <TikTokIcon size={12} style={{ color: '#fff' }} />
                                : platform.charAt(0).toUpperCase()}
                            </span>
                          ))}
                          {(ad.publisherPlatforms || []).length > 3 && (
                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                              +{ad.publisherPlatforms.length - 3}
                            </span>
                          )}
                        </div>

                        {/* Content */}
                        <div style={{ minWidth: 0 }}>
                          {primaryTitle && (
                            <div style={{
                              fontSize: '13px',
                              fontWeight: '600',
                              color: '#111827',
                              marginBottom: '2px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {primaryTitle}
                            </div>
                          )}
                          <div style={{
                            fontSize: '12px',
                            color: '#6b7280',
                            lineHeight: '1.4',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}>
                            {truncatedBody || <span style={{ fontStyle: 'italic' }}>Pas de texte</span>}
                          </div>
                          {versionCount > 1 && (
                            <div style={{
                              marginTop: '4px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              backgroundColor: '#eef2ff',
                              color: '#4338ca',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}>
                              {versionCount} mises en ligne
                              <span>{isExpanded ? '\u25b4' : '\u25be'}</span>
                            </div>
                          )}
                        </div>

                        {/* Duration */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '12px',
                          color: ad.longevityDays > 30 ? '#059669' : '#6b7280',
                          fontWeight: ad.longevityDays > 30 ? '600' : '400'
                        }}>
                          <Clock size={12} />
                          {ad.longevityDays || 0}j
                        </div>

                        {/* Reach */}
                        <div style={{
                          fontSize: '12px',
                          color: '#6b7280'
                        }}>
                          {reach}
                        </div>

                        {/* Action */}
                        <div
                          onClick={(e) => { e.stopPropagation(); voirLeVisuel(ad); }}
                          title="Voir la publicité dans un nouvel onglet"
                        >
                          <ExternalLink size={14} style={{ color: '#9ca3af' }} />
                        </div>
                      </div>
                      {isExpanded && (
                        <div style={{
                          padding: '4px 16px 14px 96px',
                          backgroundColor: '#f9fafb',
                          borderBottom: '1px solid #f3f4f6'
                        }}>
                          <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600', marginBottom: '8px' }}>
                            {versionCount} mises en ligne de cette créa
                          </div>
                          {group.versions.map((version) => {
                            const versionDate = version.adDeliveryStartTime || version.adCreationTime;
                            return (
                              <div
                                key={version.id}
                                onClick={(e) => { e.stopPropagation(); voirLeVisuel(version); }}
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: '90px 110px 90px 70px 1fr',
                                  gap: '12px',
                                  alignItems: 'center',
                                  padding: '6px 0',
                                  fontSize: '12px',
                                  color: '#4b5563',
                                  cursor: 'pointer'
                                }}
                              >
                                <span style={{ color: version.isActive ? '#166534' : '#991b1b', fontWeight: '600' }}>
                                  {version.isActive ? 'Active' : 'Stop'}
                                </span>
                                <span style={{ fontFamily: 'monospace', color: '#9ca3af' }} title={version.externalAdId}>
                                  {version.externalAdId ? `...${version.externalAdId.slice(-6)}` : '-'}
                                </span>
                                <span>
                                  {versionDate ? new Date(versionDate).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-'}
                                </span>
                                <span>{version.longevityDays || 0}j</span>
                                <span>{version.euTotalReach != null ? version.euTotalReach.toLocaleString('fr-FR') : '-'} de reach</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && stats && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
              {/* Volume timeline */}
              <div style={{
                padding: '20px',
                borderRadius: '12px',
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                  Volume de pubs actives
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={stats.adVolumeByDay}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(d) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="active" stroke="#3b82f6" fill="#93c5fd" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Platform breakdown */}
              <div style={{
                padding: '20px',
                borderRadius: '12px',
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                  Répartition par plateforme
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={stats.platformBreakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      dataKey="count"
                      nameKey="platform"
                      label={({ platform, percent }) => `${platform} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {stats.platformBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Longevity histogram */}
              <div style={{
                padding: '20px',
                borderRadius: '12px',
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb',
                gridColumn: 'span 2'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                  Distribution de la longévité
                </h3>
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
                  Les pubs qui durent longtemps sont probablement performantes (seul proxy disponible)
                </p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={stats.longevityHistogram}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Le ciblage publié au titre du DSA : la seule chose que la bibliothèque dise
                  de l'audience visée, et qu'aucun écran ne lisait. */}
              <VerdictLargeurCiblage ciblage={stats.ciblageDsa} />

              <PayeursDsa payeurs={stats.payeursDsa} />

              {/* ⚠️ Répartition démographique — réservée aux publicités politiques.
                  Mesuré : `demographicDistribution` vaut 0 sur 231 lignes. Ce bloc était
                  conditionné sur `length > 0` : il ne s'affichait donc JAMAIS, et son absence
                  se lisait « rien à signaler » plutôt que « pas mesuré ». */}
              {stats.demographicBreakdown && stats.demographicBreakdown.length > 0 ? (
                <div style={{
                  padding: '20px',
                  borderRadius: '12px',
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb'
                }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                    Répartition démographique
                  </h3>
                  <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
                    Segments d'audience ciblés (pubs politiques/sociales uniquement)
                  </p>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={stats.demographicBreakdown} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis type="number" tick={{ fontSize: 11 }} unit="%" />
                      <YAxis
                        type="category"
                        dataKey={(d) => `${d.age} ${d.gender === 'female' ? '♀' : d.gender === 'male' ? '♂' : ''}`}
                        tick={{ fontSize: 11 }}
                        width={80}
                      />
                      <Tooltip formatter={(value) => `${value}%`} />
                      <Bar dataKey="avgPercentage" fill="#ec4899" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <MesureNonPubliee
                  titre="Répartition démographique"
                  bloc={stats.mesuresReservees?.demographie}
                />
              )}

              {/* ⚠️ Même absence muette, même correction : `deliveryByRegion` vaut 0 sur 231. */}
              {stats.regionBreakdown && stats.regionBreakdown.length > 0 ? (
                <div style={{
                  padding: '20px',
                  borderRadius: '12px',
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb'
                }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                    Répartition géographique
                  </h3>
                  <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
                    Régions où les pubs sont diffusées
                  </p>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={stats.regionBreakdown} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis type="number" tick={{ fontSize: 11 }} unit="%" />
                      <YAxis
                        type="category"
                        dataKey="region"
                        tick={{ fontSize: 10 }}
                        width={120}
                      />
                      <Tooltip formatter={(value) => `${value}%`} />
                      <Bar dataKey="avgPercentage" fill="#0891b2" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <MesureNonPubliee
                  titre="Répartition géographique"
                  bloc={stats.mesuresReservees?.geographie}
                />
              )}

              {/* ⚠️ La troisième absence muette, de même nature : `spend` n'est servi que pour
                  les publicités politiques et sociétales. */}
              {stats.spendSummary ? (
                <div style={{
                  padding: '20px',
                  borderRadius: '12px',
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb'
                }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                    Budget publicitaire estimé
                  </h3>
                  <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
                    Fourchette de dépenses (pubs politiques/sociales uniquement)
                  </p>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    padding: '16px',
                    backgroundColor: '#f0fdf4',
                    borderRadius: '8px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', color: '#6b7280' }}>Estimation totale</span>
                      <span style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>
                        {stats.spendSummary.totalLower.toLocaleString()} - {stats.spendSummary.totalUpper.toLocaleString()} {stats.spendSummary.currency}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '12px', color: '#9ca3af' }}>Basé sur</span>
                      <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                        {stats.spendSummary.adsCount} pub(s) avec données de dépenses
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <MesureNonPubliee
                  titre="Budget publicitaire"
                  bloc={stats.mesuresReservees?.depense}
                />
              )}
            </div>
          )}

          {/* Onglet Ciblage — comment ce concurrent construit ses audiences. */}
          {activeTab === 'ciblage' && (
            <OngletCiblage
              concurrent={selectedCompetitor}
              token={token}
              readOnly={readOnly}
            />
          )}

          {/* Insights Tab */}
          {activeTab === 'insights' && !readOnly && (
            <div style={{
              padding: '24px',
              borderRadius: '12px',
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb'
            }}>
              {!insights ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Sparkles size={48} style={{ color: '#8b5cf6', marginBottom: '16px' }} />
                  <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                    Insights IA
                  </h3>
                  <p style={{ color: '#6b7280', marginBottom: '20px', maxWidth: '400px', margin: '0 auto 20px' }}>
                    Générez une analyse IA des publicités : angles créatifs, formats testés, stratégie en cours.
                  </p>
                  <button
                    onClick={handleGenerateInsights}
                    disabled={loadingInsights}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: '#8b5cf6',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: loadingInsights ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loadingInsights ? (
                      <>
                        <RefreshCw size={18} className="spin" />
                        Analyse en cours...
                      </>
                    ) : (
                      <>
                        <Zap size={18} />
                        Générer les insights
                      </>
                    )}
                  </button>
                  <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '12px' }}>
                    Consomme des tokens IA
                  </p>
                </div>
              ) : (
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '20px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Sparkles size={20} style={{ color: '#8b5cf6' }} />
                      <span style={{ fontWeight: '600' }}>
                        Insights pour {insights.competitor.name}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                      {insights.metadata.adsAnalyzed} pubs analysées • {insights.metadata.tokensUsed} tokens
                    </div>
                  </div>
                  {/* L'analyse arrive en Markdown : titres, tableaux GFM, citations et blocs
                      de code. Affichée en texte brut, elle laissait ses `##`, `**` et `|`
                      à l'écran. */}
                  <div className="kc-markdown">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        // Un tableau large doit défiler dans sa boîte plutôt qu'élargir
                        // le panneau : remark ne fournit pas ce conteneur, on l'ajoute.
                        table: ({ node, ...props }) => (
                          <div className="kc-markdown-tableau">
                            <table {...props} />
                          </div>
                        ),
                        // Les liens d'une analyse pointent hors de l'application
                        a: ({ node, ...props }) => (
                          <a {...props} target="_blank" rel="noopener noreferrer" />
                        )
                      }}
                    >
                      {insights.insights}
                    </ReactMarkdown>
                  </div>
                  <button
                    onClick={handleGenerateInsights}
                    disabled={loadingInsights}
                    style={{
                      marginTop: '20px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#fff',
                      color: '#374151',
                      fontSize: '13px',
                      cursor: loadingInsights ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <RefreshCw size={14} />
                    Régénérer
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Add Competitor Modal */}
      {showAddModal && !readOnly && ReactDOM.createPortal(
        <div
          onClick={() => setShowAddModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div
            onClick={(e) => {
              e.stopPropagation();
              setShowPageResults(false);
            }}
            style={{
              backgroundColor: '#fff',
              borderRadius: '16px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
          <div style={{ padding: '24px', overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                Ajouter une page
              </h2>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleAddCompetitor}>
              {/* Type selector */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Type
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setNewPageType('CLIENT')}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: newPageType === 'CLIENT' ? '2px solid #22c55e' : '1px solid #e5e7eb',
                      backgroundColor: newPageType === 'CLIENT' ? '#f0fdf4' : '#fff',
                      color: newPageType === 'CLIENT' ? '#22c55e' : '#6b7280',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    Ma marque
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewPageType('COMPETITOR')}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: newPageType === 'COMPETITOR' ? '2px solid #f59e0b' : '1px solid #e5e7eb',
                      backgroundColor: newPageType === 'COMPETITOR' ? '#fffbeb' : '#fff',
                      color: newPageType === 'COMPETITOR' ? '#f59e0b' : '#6b7280',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    Concurrent
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  {newPageType === 'CLIENT' ? 'Nom de ma marque *' : 'Nom du concurrent *'}
                </label>
                <input
                  type="text"
                  value={newCompetitorName}
                  onChange={(e) => setNewCompetitorName(e.target.value)}
                  placeholder={newPageType === 'CLIENT' ? 'Ex: Ma Boutique' : 'Ex: Zalando'}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  required
                />
              </div>

              {/* Platform selector */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Plateforme
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setNewPagePlatform('META');
                      setNewPageId('');
                      setNewPageLabel('');
                      setPageSearchTerm('');
                      setPageSearchResults([]);
                      setSearchError(null);
                    }}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '12px',
                      borderRadius: '8px',
                      border: newPagePlatform === 'META' ? '2px solid #1877f2' : '1px solid #e5e7eb',
                      backgroundColor: newPagePlatform === 'META' ? '#eff6ff' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <Facebook size={20} style={{ color: '#1877f2' }} />
                    <span style={{ fontWeight: newPagePlatform === 'META' ? '600' : '400', color: '#374151' }}>
                      Meta
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNewPagePlatform('TIKTOK');
                      setNewPageId('');
                      setNewPageLabel('');
                      setPageSearchTerm('');
                      setPageSearchResults([]);
                      setSearchError(null);
                    }}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '12px',
                      borderRadius: '8px',
                      border: newPagePlatform === 'TIKTOK' ? '2px solid #000' : '1px solid #e5e7eb',
                      backgroundColor: newPagePlatform === 'TIKTOK' ? '#f5f5f5' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <TikTokIcon size={20} style={{ color: '#000' }} />
                    <span style={{ fontWeight: newPagePlatform === 'TIKTOK' ? '600' : '400', color: '#374151' }}>
                      TikTok
                    </span>
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '16px', position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '500' }}>
                    {newPagePlatform === 'TIKTOK' ? 'Annonceur TikTok *' : 'Page Facebook *'}
                  </label>
                  <button
                    type="button"
                    onClick={() => setManualMode(!manualMode)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#3b82f6',
                      fontSize: '11px',
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }}
                  >
                    {manualMode ? 'Utiliser la recherche' : 'Saisie manuelle'}
                  </button>
                </div>

                {manualMode ? (
                  /* Manual mode - direct Page ID or URL input */
                  <>
                    <input
                      type="text"
                      value={newPageId}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (newPagePlatform === 'META') {
                          // Try to extract page ID from URL for Meta
                          const extracted = extractFacebookPageId(value);
                          if (extracted && value.includes('facebook.com')) {
                            setNewPageId(extracted.id);
                            // Auto-fill label from username if not numeric
                            if (extracted.type === 'username' && !newPageLabel) {
                              setNewPageLabel(extracted.id);
                            }
                          } else {
                            setNewPageId(value);
                          }
                        } else {
                          // TikTok - just set the value directly
                          setNewPageId(value);
                        }
                      }}
                      onPaste={(e) => {
                        // Handle paste event for better UX (Meta only)
                        if (newPagePlatform === 'META') {
                          setTimeout(() => {
                            const value = e.target.value;
                            const extracted = extractFacebookPageId(value);
                            if (extracted) {
                              setNewPageId(extracted.id);
                              if (extracted.type === 'username' && !newPageLabel) {
                                setNewPageLabel(extracted.id);
                              }
                            }
                          }, 0);
                        }
                      }}
                      placeholder={newPagePlatform === 'TIKTOK'
                        ? "Business ID TikTok (ex: 7123456789)"
                        : "URL ou ID (ex: facebook.com/Nike ou 123456789)"}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        marginBottom: '8px'
                      }}
                    />
                    <input
                      type="text"
                      value={newPageLabel}
                      onChange={(e) => setNewPageLabel(e.target.value)}
                      placeholder={newPagePlatform === 'TIKTOK' ? "Nom de l'annonceur (optionnel)" : "Nom de la page (optionnel)"}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>
                      {newPagePlatform === 'TIKTOK'
                        ? "Entrez le Business ID TikTok de l'annonceur."
                        : "Collez l'URL de la page Facebook - l'ID sera extrait automatiquement."}
                    </p>
                  </>
                ) : (
                  /* Search mode */
                  <>
                    <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        value={pageSearchTerm}
                        onChange={(e) => handlePageSearchInput(e.target.value)}
                        onFocus={() => pageSearchResults.length > 0 && setShowPageResults(true)}
                        placeholder={newPagePlatform === 'TIKTOK'
                          ? "Rechercher un annonceur TikTok"
                          : "Rechercher une page (ex: Zalando)"}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          paddingLeft: '36px',
                          borderRadius: '8px',
                          border: `1px solid ${newPageId ? '#22c55e' : '#e5e7eb'}`,
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                      <div style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#9ca3af'
                      }}>
                        {searchingPages ? (
                          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                        ) : (
                          <Search size={16} />
                        )}
                      </div>
                    </div>

                    {/* Search error message */}
                    {searchError && (
                      <div style={{
                        marginTop: '6px',
                        padding: '8px 10px',
                        backgroundColor: '#fef3c7',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#92400e'
                      }}>
                        {searchError}
                      </div>
                    )}

                    {/* Search results dropdown */}
                    {showPageResults && !searchError && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        marginTop: '4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 10,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}>
                        {searchingPages ? (
                          <div style={{ padding: '12px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
                            Recherche en cours...
                          </div>
                        ) : pageSearchResults.length === 0 ? (
                          <div style={{ padding: '12px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
                            Aucune page trouvée
                          </div>
                        ) : (
                          pageSearchResults.map((page) => (
                            <div
                              key={page.pageId}
                              onClick={() => handleSelectPage(page)}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f3f4f6',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              {newPagePlatform === 'TIKTOK' ? (
                                <TikTokIcon size={16} style={{ color: '#000', flexShrink: 0 }} />
                              ) : (
                                <Facebook size={16} style={{ color: '#1877f2', flexShrink: 0 }} />
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: '500', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  {page.pageName}
                                  {page.fromLookup && (
                                    <span style={{ fontSize: '9px', padding: '1px 4px', backgroundColor: '#dbeafe', color: '#1d4ed8', borderRadius: '3px', fontWeight: '500' }}>
                                      Graph API
                                    </span>
                                  )}
                                </div>
                                <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                  ID: {page.pageId}
                                  {page.adCount > 0 && ` • ${page.adCount} pub${page.adCount > 1 ? 's' : ''}`}
                                  {page.category && ` • ${page.category}`}
                                  {page.fanCount && ` • ${page.fanCount.toLocaleString()} fans`}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {/* Selected page indicator */}
                    {newPageId && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginTop: '6px',
                        fontSize: '12px',
                        color: newPagePlatform === 'META' && !/^\d+$/.test(newPageId) ? '#f59e0b' : '#22c55e'
                      }}>
                        {newPagePlatform === 'TIKTOK' ? (
                          <TikTokIcon size={14} />
                        ) : (
                          <Facebook size={14} />
                        )}
                        <span>
                          {newPagePlatform === 'META' && !/^\d+$/.test(newPageId)
                            ? `ID invalide : ${newPageId} (doit être numérique)`
                            : `${newPagePlatform === 'TIKTOK' ? 'Annonceur sélectionné' : 'Page sélectionnée'} : ${newPageLabel || newPageId}`
                          }
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setNewPageId('');
                            setNewPageLabel('');
                            setPageSearchTerm('');
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#dc2626',
                            padding: '2px',
                            marginLeft: 'auto'
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    )}

                    {!searchError && (
                      <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>
                        {newPagePlatform === 'TIKTOK'
                          ? "Recherchez un annonceur TikTok ayant diffusé des publicités"
                          : "Recherchez une page Facebook ayant diffusé des publicités"}
                      </p>
                    )}
                  </>
                )}
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  {newPagePlatform === 'TIKTOK' ? 'Région (code ISO)' : 'Pays (codes ISO, séparés par virgule)'}
                </label>
                <input
                  type="text"
                  value={newPageCountries}
                  onChange={(e) => setNewPageCountries(e.target.value)}
                  placeholder={newPagePlatform === 'TIKTOK' ? 'FR' : 'FR, BE, DE'}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                  {newPagePlatform === 'TIKTOK'
                    ? "Région pour la recherche TikTok (un seul code)"
                    : "Seules les pubs ciblant ces pays seront récupérées"}
                </p>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    backgroundColor: '#fff',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#3b82f6',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Ajouter
                </button>
              </div>
            </form>
          </div>
          </div>
        </div>,
        document.body
      )}

      {/* Settings Modal - Pages Table */}
      {showSettingsModal && !readOnly && ReactDOM.createPortal(
        <div
          onClick={() => {
            setShowSettingsModal(false);
            cancelEditPage();
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#fff',
              borderRadius: '16px',
              width: '95%',
              maxWidth: '900px',
              maxHeight: '85vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                  Pages auditées
                </h2>
                <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0' }}>
                  {clients.find(c => c.id === selectedClient)?.name || 'Client'} - {competitors.length} page{competitors.length !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowSettingsModal(false);
                  cancelEditPage();
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '8px'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Table */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0' }}>
              {competitors.length === 0 ? (
                <div style={{
                  padding: '60px 24px',
                  textAlign: 'center',
                  color: '#6b7280'
                }}>
                  <Settings size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
                  <p style={{ fontSize: '15px', marginBottom: '8px' }}>Aucune page configurée</p>
                  <p style={{ fontSize: '13px' }}>Cliquez sur "Ajouter une page" pour commencer</p>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Type</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Nom</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Plateforme</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>ID / URL</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Pays</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Pubs</th>
                      <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {competitors.map((page) => {
                      const isEditing = editingPageId === page.id;
                      const firstSubPage = page.pages?.[0];
                      const platform = firstSubPage?.platform || 'META';
                      const countries = firstSubPage?.countries || ['FR'];
                      const pageUrl = getPageUrl(page);

                      return (
                        <tr
                          key={page.id}
                          style={{
                            borderBottom: '1px solid #f3f4f6',
                            backgroundColor: isEditing ? '#f0f9ff' : 'transparent'
                          }}
                        >
                          {/* Type */}
                          <td style={{ padding: '12px 16px' }}>
                            {isEditing ? (
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <button
                                  type="button"
                                  onClick={() => setEditType('CLIENT')}
                                  style={{
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    border: editType === 'CLIENT' ? '2px solid #22c55e' : '1px solid #e5e7eb',
                                    backgroundColor: editType === 'CLIENT' ? '#f0fdf4' : '#fff',
                                    color: editType === 'CLIENT' ? '#22c55e' : '#6b7280',
                                    fontSize: '11px',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Ma marque
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditType('COMPETITOR')}
                                  style={{
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    border: editType === 'COMPETITOR' ? '2px solid #f59e0b' : '1px solid #e5e7eb',
                                    backgroundColor: editType === 'COMPETITOR' ? '#fffbeb' : '#fff',
                                    color: editType === 'COMPETITOR' ? '#f59e0b' : '#6b7280',
                                    fontSize: '11px',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Concurrent
                                </button>
                              </div>
                            ) : (
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 8px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '500',
                                backgroundColor: page.type === 'CLIENT' ? '#f0fdf4' : '#fffbeb',
                                color: page.type === 'CLIENT' ? '#22c55e' : '#f59e0b'
                              }}>
                                {page.type === 'CLIENT' ? '⭐ Ma marque' : 'Concurrent'}
                              </span>
                            )}
                          </td>

                          {/* Nom */}
                          <td style={{ padding: '12px 16px' }}>
                            {isEditing ? (
                              <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '6px 10px',
                                  borderRadius: '6px',
                                  border: '1px solid #3b82f6',
                                  fontSize: '13px',
                                  boxSizing: 'border-box'
                                }}
                                autoFocus
                              />
                            ) : (
                              <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                                {page.name}
                              </span>
                            )}
                          </td>

                          {/* Plateforme */}
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              fontSize: '13px',
                              color: '#374151'
                            }}>
                              {platform === 'TIKTOK' ? (
                                <TikTokIcon size={16} style={{ color: '#000' }} />
                              ) : (
                                <Facebook size={16} style={{ color: '#1877f2' }} />
                              )}
                              {platform === 'TIKTOK' ? 'TikTok' : 'Meta'}
                            </span>
                          </td>

                          {/* ID / URL */}
                          <td style={{ padding: '12px 16px' }}>
                            {pageUrl ? (
                              <a
                                href={pageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  fontSize: '12px',
                                  color: '#3b82f6',
                                  textDecoration: 'none',
                                  maxWidth: '180px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                                title={pageUrl}
                              >
                                <Globe size={12} />
                                {firstSubPage?.externalId}
                                <ExternalLink size={10} />
                              </a>
                            ) : (
                              <span style={{ fontSize: '12px', color: '#9ca3af' }}>-</span>
                            )}
                          </td>

                          {/* Pays */}
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{
                              fontSize: '12px',
                              color: '#6b7280',
                              backgroundColor: '#f3f4f6',
                              padding: '2px 6px',
                              borderRadius: '4px'
                            }}>
                              {countries.join(', ')}
                            </span>
                          </td>

                          {/* Pubs */}
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>
                              {page.totalAds || 0}
                            </span>
                          </td>

                          {/* Actions */}
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                            {isEditing ? (
                              <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                <button
                                  onClick={() => savePageEdit(page.id)}
                                  title="Enregistrer"
                                  style={{
                                    padding: '6px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    backgroundColor: '#22c55e',
                                    color: '#fff',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <Check size={14} />
                                </button>
                                <button
                                  onClick={cancelEditPage}
                                  title="Annuler"
                                  style={{
                                    padding: '6px',
                                    borderRadius: '6px',
                                    border: '1px solid #e5e7eb',
                                    backgroundColor: '#fff',
                                    color: '#6b7280',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                <button
                                  onClick={() => startEditPage(page)}
                                  title="Modifier"
                                  style={{
                                    padding: '6px',
                                    borderRadius: '6px',
                                    border: '1px solid #e5e7eb',
                                    backgroundColor: '#fff',
                                    color: '#6b7280',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <Edit2 size={14} />
                                </button>
                                <button
                                  onClick={() => handleDeleteCompetitor(page.id)}
                                  title="Supprimer"
                                  style={{
                                    padding: '6px',
                                    borderRadius: '6px',
                                    border: '1px solid #fecaca',
                                    backgroundColor: '#fff',
                                    color: '#dc2626',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <button
                onClick={() => {
                  setShowSettingsModal(false);
                  setShowAddModal(true);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#3b82f6',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                <Plus size={16} />
                Ajouter une page
              </button>
              <button
                onClick={() => {
                  setShowSettingsModal(false);
                  cancelEditPage();
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: '#fff',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Spin animation style */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default CompetitorAdWatchTool;
